export type AiLanguagePracticePage = {
  slug: string;
  name: string;
  code: string;
  family: string;
  script: string;
  learnerIntent: string;
  commonBlockers: string[];
  learnerMistakes: string[];
  pronunciationTargets: string[];
  speakingScenarios: string[];
  speakingDrills: string[];
  firstSessionPrompt: string;
  tutorFocus: string[];
  practicePlan: string[];
};

const pageData: AiLanguagePracticePage[] = [
  {
    slug: "spanish",
    name: "Spanish",
    code: "es-US",
    family: "Romance",
    script: "Latin",
    learnerIntent:
      "Spanish learners usually have enough apps for vocabulary, but not enough low-pressure speaking time.",
    commonBlockers: [
      "Thinking in English before every sentence",
      "Freezing when native speakers answer quickly",
      "Using textbook phrases that sound stiff in conversation",
    ],
    learnerMistakes: [
      "Using ser and estar interchangeably when describing temporary states",
      "Dropping small connector words that make answers sound natural",
      "Answering with full written sentences instead of short spoken replies",
    ],
    pronunciationTargets: ["rolled or tapped r", "vowel clarity", "syllable stress"],
    speakingScenarios: ["ordering food", "travel help", "small talk", "weekend plans"],
    speakingDrills: [
      "Give a two-sentence answer about yesterday, then repeat it with one corrected verb tense.",
      "Ask for directions and keep the roleplay going for three follow-up turns.",
      "Describe a weekend plan once formally and once like a casual voice note.",
    ],
    firstSessionPrompt:
      "Roleplay ordering food in Spanish. Ask one follow-up question after each answer and correct only the most unnatural phrase.",
    tutorFocus: ["verb tense repair", "pronunciation rhythm", "faster everyday replies"],
    practicePlan: [
      "Start with five-minute self-introduction loops.",
      "Repeat common travel and food situations until replies feel automatic.",
      "Ask the tutor to correct one recurring grammar mistake per session.",
    ],
  },
  {
    slug: "english",
    name: "English",
    code: "en-US",
    family: "Germanic",
    script: "Latin",
    learnerIntent:
      "English learners often know grammar rules but need more real conversation practice without embarrassment.",
    commonBlockers: [
      "Translating silently before speaking",
      "Losing confidence when pronunciation is corrected",
      "Knowing words passively but not using them in conversation",
    ],
    learnerMistakes: [
      "Putting stress on the wrong syllable in high-frequency work words",
      "Using correct grammar that still sounds too formal for conversation",
      "Avoiding short filler phrases that keep a live reply moving",
    ],
    pronunciationTargets: ["word stress", "th sounds", "connected speech"],
    speakingScenarios: ["work meetings", "introductions", "travel", "daily opinions"],
    speakingDrills: [
      "Introduce your work in three sentences, then make the same answer more natural.",
      "Give an opinion and answer two follow-up questions without pausing to translate.",
      "Repeat a corrected sentence with connected speech instead of reading each word separately.",
    ],
    firstSessionPrompt:
      "Run a short English work-introduction roleplay. Correct one pronunciation issue and one more natural phrase after each reply.",
    tutorFocus: ["natural phrasing", "pronunciation clarity", "conversation confidence"],
    practicePlan: [
      "Practice short work and travel conversations first.",
      "Ask for natural alternatives to sentences that sound translated.",
      "Use repeat sessions to turn passive vocabulary into active speech.",
    ],
  },
  {
    slug: "french",
    name: "French",
    code: "fr-FR",
    family: "Romance",
    script: "Latin",
    learnerIntent:
      "French learners need repeated speaking practice to connect spelling, liaison, rhythm, and everyday replies.",
    commonBlockers: [
      "Reading French better than speaking it",
      "Struggling with liaison and silent letters",
      "Avoiding conversation because pronunciation feels exposed",
    ],
    learnerMistakes: [
      "Pronouncing silent endings that should disappear in speech",
      "Using the wrong gender agreement after changing a noun",
      "Overusing literal English word order in everyday French replies",
    ],
    pronunciationTargets: ["u versus ou", "nasal vowels", "silent endings and liaison"],
    speakingScenarios: ["cafes", "directions", "travel check-ins", "opinions"],
    speakingDrills: [
      "Order at a cafe, then repeat the corrected sentence with liaison marked by the tutor.",
      "Give directions using two short clauses instead of one memorized phrase.",
      "State an opinion about a film or meal and repair one gender agreement mistake.",
    ],
    firstSessionPrompt:
      "Roleplay ordering at a French cafe. Keep the conversation in French and correct liaison, silent endings, and one grammar point.",
    tutorFocus: ["pronunciation confidence", "gender agreement", "natural sentence flow"],
    practicePlan: [
      "Use short cafe and travel roleplays to reduce pronunciation pressure.",
      "Practice one tense at a time inside real conversation.",
      "Review corrected phrases aloud before starting the next session.",
    ],
  },
  {
    slug: "japanese",
    name: "Japanese",
    code: "ja-JP",
    family: "Japonic",
    script: "Japanese writing system",
    learnerIntent:
      "Japanese learners need safe speaking reps because politeness, particles, and word order are hard to automate from study alone.",
    commonBlockers: [
      "Knowing grammar points but not forming sentences quickly",
      "Avoiding speech because particles feel risky",
      "Practicing reading more than live output",
    ],
    learnerMistakes: [
      "Choosing a particle from memory instead of the role it plays in the sentence",
      "Using a polite form in one sentence and a casual form in the next",
      "Building long English-shaped sentences instead of short Japanese replies",
    ],
    pronunciationTargets: ["long vowels", "pitch accent basics", "clear particles"],
    speakingScenarios: ["self-introductions", "restaurant orders", "directions", "hobby talk"],
    speakingDrills: [
      "Give a polite self-introduction and have the tutor correct only particles.",
      "Answer three hobby questions with short replies before expanding one answer.",
      "Roleplay a restaurant order and repeat corrected long vowels aloud.",
    ],
    firstSessionPrompt:
      "Practice a polite Japanese self-introduction. Ask simple follow-ups and correct particles without stopping the roleplay.",
    tutorFocus: ["particles", "politeness level", "short natural replies"],
    practicePlan: [
      "Start with polite self-introduction and ordering loops.",
      "Practice short answers before longer explanations.",
      "Ask for particle corrections without stopping the conversation flow.",
    ],
  },
  {
    slug: "german",
    name: "German",
    code: "de-DE",
    family: "Germanic",
    script: "Latin",
    learnerIntent:
      "German learners need speaking practice that makes cases, word order, and compound words usable under pressure.",
    commonBlockers: [
      "Overthinking cases before speaking",
      "Putting the verb in the wrong place",
      "Understanding written German better than fast conversation",
    ],
    learnerMistakes: [
      "Moving the verb too late or too early in subordinate clauses",
      "Choosing an article before checking the case role",
      "Flattening umlauts so common words become hard to understand",
    ],
    pronunciationTargets: ["ch sounds", "umlauts", "final consonant clarity"],
    speakingScenarios: ["appointments", "travel", "shopping", "work introductions"],
    speakingDrills: [
      "Book an appointment and have the tutor stop only for verb-position repairs.",
      "Describe a daily routine using three short sentences with corrected articles.",
      "Repeat shopping phrases with umlauts and final consonants exaggerated once.",
    ],
    firstSessionPrompt:
      "Roleplay making a German appointment. Correct verb position first, then one pronunciation issue after each answer.",
    tutorFocus: ["case repair", "verb position", "clear pronunciation"],
    practicePlan: [
      "Practice short appointment and shopping scenarios.",
      "Use correction loops for one case pattern at a time.",
      "Retell simple daily routines to train verb placement.",
    ],
  },
  {
    slug: "korean",
    name: "Korean",
    code: "ko-KR",
    family: "Koreanic",
    script: "Hangul",
    learnerIntent:
      "Korean learners often need help moving from memorized phrases into flexible speech with the right politeness level.",
    commonBlockers: [
      "Choosing the right speech level",
      "Forming natural endings quickly",
      "Relying on romanization too long",
    ],
    learnerMistakes: [
      "Mixing polite and casual endings in the same exchange",
      "Letting batchim consonants disappear when speaking quickly",
      "Following romanization habits instead of Hangul sound patterns",
    ],
    pronunciationTargets: ["batchim endings", "double consonants", "sentence endings"],
    speakingScenarios: ["introductions", "ordering", "K-drama opinions", "travel help"],
    speakingDrills: [
      "Order politely and ask the tutor to correct only endings for five turns.",
      "Give a short K-drama opinion and repeat one corrected batchim ending.",
      "Switch a memorized sentence from casual to polite speech with tutor feedback.",
    ],
    firstSessionPrompt:
      "Run a polite Korean ordering roleplay. Keep the prompts short and correct speech level plus sentence endings.",
    tutorFocus: ["speech levels", "sentence endings", "Hangul pronunciation"],
    practicePlan: [
      "Practice polite everyday conversations before casual speech.",
      "Repeat common sentence endings until they become automatic.",
      "Use Hangul support instead of romanization whenever possible.",
    ],
  },
  {
    slug: "mandarin",
    name: "Mandarin",
    code: "cmn-CN",
    family: "Sino-Tibetan",
    script: "Chinese characters",
    learnerIntent:
      "Mandarin learners need tone-aware speaking practice and simple conversation loops that turn study into usable speech.",
    commonBlockers: [
      "Tone confidence in live speech",
      "Knowing words but missing sentence rhythm",
      "Avoiding spontaneous replies",
    ],
    learnerMistakes: [
      "Producing the right syllable with the wrong tone under conversation pressure",
      "Skipping measure words when making practical requests",
      "Answering with isolated vocabulary instead of complete short phrases",
    ],
    pronunciationTargets: ["four tones", "neutral tone", "initial consonants"],
    speakingScenarios: ["introductions", "food orders", "directions", "shopping"],
    speakingDrills: [
      "Order food with short phrases and repeat each corrected tone once.",
      "Ask for a price and answer a follow-up using one corrected measure word.",
      "Give a two-sentence self-introduction while the tutor tracks tone drift.",
    ],
    firstSessionPrompt:
      "Practice a Mandarin food-ordering roleplay. Correct tones in short phrases and repeat the corrected sentence once.",
    tutorFocus: ["tone awareness", "measure words", "short fluent replies"],
    practicePlan: [
      "Start with short high-frequency phrases and repeat them aloud.",
      "Ask for tone and rhythm corrections during simple roleplays.",
      "Build from fixed sentence frames into flexible answers.",
    ],
  },
  {
    slug: "dutch",
    name: "Dutch",
    code: "nl-NL",
    family: "Germanic",
    script: "Latin",
    learnerIntent:
      "Dutch learners often understand a lot but need practice replying before Dutch speakers switch to English.",
    commonBlockers: [
      "Dutch speakers switching to English too quickly",
      "Pronouncing guttural sounds confidently",
      "Using German or English word order by accident",
    ],
    learnerMistakes: [
      "Using English-style word order after time and place phrases",
      "Softening g and ch so words lose their Dutch shape",
      "Answering too slowly and triggering a switch back to English",
    ],
    pronunciationTargets: ["g and ch", "ui vowel", "sch clusters"],
    speakingScenarios: ["introductions", "shopping", "appointments", "small talk"],
    speakingDrills: [
      "Keep a shop conversation in Dutch for five turns before asking for corrections.",
      "Answer small-talk questions with time phrases so the tutor can repair word order.",
      "Repeat g, ch, and sch words inside full sentences instead of isolated sounds.",
    ],
    firstSessionPrompt:
      "Roleplay a short Dutch shop conversation. Keep going in Dutch and correct word order plus g/ch pronunciation.",
    tutorFocus: ["word order", "pronunciation", "fast practical replies"],
    practicePlan: [
      "Practice short service conversations so replies come faster.",
      "Train pronunciation in small loops instead of long drills.",
      "Use everyday Dutch topics before advanced grammar explanations.",
    ],
  },
  {
    slug: "arabic",
    name: "Arabic",
    code: "ar-XA",
    family: "Semitic",
    script: "Arabic",
    learnerIntent:
      "Arabic learners need speaking practice that handles script, pronunciation, and the gap between standard Arabic and spoken use.",
    commonBlockers: [
      "Feeling stuck between standard Arabic and dialects",
      "Pronouncing unfamiliar consonants",
      "Reading slowly and avoiding speech",
    ],
    learnerMistakes: [
      "Using a formal phrase where a practical spoken phrase fits better",
      "Flattening emphatic consonants until key words sound ambiguous",
      "Pausing at every written vowel instead of practicing spoken rhythm",
    ],
    pronunciationTargets: ["emphatic consonants", "ayn and ghayn", "short vowels"],
    speakingScenarios: ["greetings", "market conversations", "travel help", "family talk"],
    speakingDrills: [
      "Practice greetings and ask the tutor to label each phrase as formal or spoken.",
      "Roleplay a market exchange with one corrected emphatic consonant per turn.",
      "Answer family-topic questions in short phrases before expanding the answer.",
    ],
    firstSessionPrompt:
      "Practice Arabic greetings and a market conversation. Clarify whether the phrase is formal or practical spoken Arabic.",
    tutorFocus: ["sound production", "useful spoken phrases", "sentence patterns"],
    practicePlan: [
      "Start with greetings and daily survival phrases.",
      "Practice one conversation context repeatedly before adding complexity.",
      "Use the tutor to separate formal phrases from practical spoken phrases.",
    ],
  },
  {
    slug: "polish",
    name: "Polish",
    code: "pl-PL",
    family: "Slavic",
    script: "Latin",
    learnerIntent:
      "Polish learners need patient speaking practice because cases, consonant clusters, and endings can slow output.",
    commonBlockers: [
      "Overthinking noun cases",
      "Avoiding difficult consonant clusters",
      "Knowing vocabulary but not speaking quickly",
    ],
    learnerMistakes: [
      "Guessing endings from tables instead of the sentence role",
      "Skipping consonants inside clusters when speaking faster",
      "Using dictionary-form words where a natural spoken phrase needs an ending",
    ],
    pronunciationTargets: ["sz and cz", "rz and z-dot", "consonant clusters"],
    speakingScenarios: ["introductions", "family", "shopping", "daily routines"],
    speakingDrills: [
      "Introduce your family and have the tutor correct one case ending at a time.",
      "Roleplay shopping with short answers that repeat difficult clusters aloud.",
      "Describe a daily routine and repair endings without switching back to English.",
    ],
    firstSessionPrompt:
      "Roleplay a Polish introduction and shopping exchange. Correct case endings in context and repeat difficult clusters.",
    tutorFocus: ["case endings", "pronunciation clarity", "practical sentence frames"],
    practicePlan: [
      "Practice short daily-routine sentences before complex topics.",
      "Ask for case corrections in context, not isolated tables.",
      "Repeat corrected phrases aloud to build mouth memory.",
    ],
  },
];

export const aiLanguagePracticePages = pageData;

export function getAiLanguagePracticePage(slug: string): AiLanguagePracticePage | undefined {
  return aiLanguagePracticePages.find((page) => page.slug === slug);
}

export function getAiTutorPath(page: AiLanguagePracticePage): string {
  return `/ai/${page.slug}-tutor`;
}

export function getSpeakingPracticePath(page: AiLanguagePracticePage): string {
  return `/practice/${page.slug}-speaking`;
}

export function getAiTutorStaticParams(): Array<{ slug: string }> {
  return aiLanguagePracticePages.flatMap((page) => [
    { slug: `${page.slug}-tutor` },
    { slug: page.slug },
  ]);
}

export function getSpeakingPracticeStaticParams(): Array<{ slug: string }> {
  return aiLanguagePracticePages.flatMap((page) => [
    { slug: `${page.slug}-speaking` },
    { slug: page.slug },
  ]);
}

export function normalizeAiTutorSlug(slug: string): string {
  return slug.endsWith("-tutor") ? slug.slice(0, -"-tutor".length) : slug;
}

export function normalizeSpeakingPracticeSlug(slug: string): string {
  return slug.endsWith("-speaking") ? slug.slice(0, -"-speaking".length) : slug;
}

export function isCanonicalAiTutorSlug(slug: string): boolean {
  const page = getAiLanguagePracticePage(normalizeAiTutorSlug(slug));
  return Boolean(page && slug === `${page.slug}-tutor`);
}

export function isCanonicalSpeakingPracticeSlug(slug: string): boolean {
  const page = getAiLanguagePracticePage(normalizeSpeakingPracticeSlug(slug));
  return Boolean(page && slug === `${page.slug}-speaking`);
}
